import axios from "axios";
import LOCAL_STORAGE_KEY from "../constants/localStorageKey";
import { getStorage } from "./general.util";
import ROUTES from "../constants/routes";
import { executeWithRefresh } from "../auth/tokenRefresh";

const HOST = import.meta.env.VITE_NEW_GATEWAY_HOST || "https://apis.heru.app";
const ENV = import.meta.env.VITE_ENV_HOST || "prod";

// Cada método lee el token DENTRO del closure que `executeWithRefresh` puede
// reintentar: tras un 401 → refresh, la segunda ejecución toma el token nuevo
// de storage. Si el refresh falla, el 401 llega a `onAccountError` como siempre.

export const get = async <T = any>({
  service,
  endpoint,
  options,
  skipAccountError = false,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  options?: { directURL?: string; headers?: Record<string, string> };
  skipAccountError?: boolean;
}) => {
  try {
    const url = options?.directURL || getURL(service, endpoint);

    const response = await executeWithRefresh(() =>
      axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
      }),
    );

    // A 200 with a `null`/empty body (e.g. a deleted/absent resource the API
    // answers with `null` instead of 404) leaves `response.data` null —
    // `null.resource` would throw and turn a benign empty result into a query
    // error + retry loop. Optional-chain so an empty body returns null cleanly.
    return response.data?.resource || (response.data as T);
  } catch (error: any) {
    if (skipAccountError) throw error;
    onAccountError(error);
    throw error;
  }
};

export const getBlob = async ({
  service,
  endpoint,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
}) => {
  try {
    const response = await executeWithRefresh(() =>
      axios.get<Blob>(getURL(service, endpoint), {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getUserToken()}` },
      }),
    );
    return response.data;
  } catch (error) {
    onAccountError(error);
    throw error;
  }
};

/**
 * `POST` que responde un archivo (p. ej. un PDF generado al vuelo). Un error
 * también llega como `Blob`: quien llama lee su cuerpo con `error.response.data.text()`.
 */
export const postBlob = async ({
  service,
  endpoint,
  body,
  options,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  body?: object;
  options?: { directURL?: string; headers?: Record<string, string> };
}) => {
  try {
    const url = options?.directURL || getURL(service, endpoint);
    const response = await executeWithRefresh(() =>
      axios.post<Blob>(url, body, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
      }),
    );
    return response.data;
  } catch (error) {
    onAccountError(error);
    throw error;
  }
};

export const post = async <T = any>({
  service,
  endpoint,
  body,
  options,
  skipAccountError = false,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  body: object;
  options?: { directURL?: string; headers?: Record<string, string> };
  skipAccountError?: boolean;
}) => {
  // Don't set Content-Type for FormData - let axios handle it automatically
  const isFormData = body instanceof FormData;
  const url = options?.directURL || getURL(service, endpoint);

  try {
    const { data } = await executeWithRefresh(() =>
      axios.post(url, body, {
        headers: {
          ...(!isFormData && { "Content-Type": "application/json" }),
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
      }),
    );
    return (data?.resource as T) ?? (data as T);
  } catch (error) {
    if (skipAccountError) throw error;
    return onAccountError(error);
  }
};

export const put = async <T = any>({
  service,
  endpoint,
  body,
  options,
  skipAccountError = false,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  body: object;
  options?: { directURL?: string; headers?: Record<string, string> };
  skipAccountError?: boolean;
}) => {
  // Don't set Content-Type for FormData - let axios handle it automatically
  const isFormData = body instanceof FormData;
  const url = options?.directURL || getURL(service, endpoint);

  try {
    const { data } = await executeWithRefresh(() =>
      axios.put(url, body, {
        headers: {
          ...(!isFormData && { "Content-Type": "application/json" }),
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
      }),
    );
    return (data?.resource as T) ?? (data as T);
  } catch (error) {
    if (skipAccountError) throw error;
    return onAccountError(error);
  }
};

export const patch = async <T = any>({
  service,
  endpoint,
  body,
  options,
  skipAccountError = false,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  body: object;
  options?: { directURL?: string; headers?: Record<string, string> };
  skipAccountError?: boolean;
}) => {
  // Don't set Content-Type for FormData - let axios handle it automatically
  const isFormData = body instanceof FormData;
  const url = options?.directURL || getURL(service, endpoint);

  try {
    const { data } = await executeWithRefresh(() =>
      axios.patch(url, body, {
        headers: {
          ...(!isFormData && { "Content-Type": "application/json" }),
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
      }),
    );
    return (data?.resource as T) ?? (data as T);
  } catch (error) {
    if (skipAccountError) throw error;
    return onAccountError(error);
  }
};

export const del = async <T = any>({
  service,
  endpoint,
  body,
  options,
  skipAccountError = false,
}: {
  service: keyof typeof SERVICE_CONFIG;
  endpoint: string;
  body?: object;
  options?: { directURL?: string; headers?: Record<string, string> };
  skipAccountError?: boolean;
}) => {
  try {
    const url = options?.directURL || getURL(service, endpoint);

    const response = await executeWithRefresh(() =>
      axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getUserToken()}`,
          ...options?.headers,
        },
        ...(body && { data: body }),
      }),
    );

    return response.data?.resource || (response.data as T);
  } catch (error: any) {
    if (skipAccountError) throw error;
    onAccountError(error);
    throw error;
  }
};

const LOCAL_SERVICES = (import.meta.env.VITE_LOCAL_SERVICES || "").split(",").map((s: string) => s.trim()).filter(Boolean);

const LOCAL_PORTS: Record<string, number> = Object.fromEntries(
  (import.meta.env.VITE_LOCAL_PORTS || "")
    .split(",")
    .map((pair: string) => pair.split("=").map((part: string) => part.trim()))
    .filter(([service, port]: string[]) => service && Number.isInteger(Number(port)) && Number(port) > 0)
    .map(([service, port]: string[]) => [service, Number(port)]),
);

/**
 * Origin a service answers on: `http://localhost:<port>` when it runs locally
 * (`VITE_ENV_HOST=dev`, or listed in `VITE_LOCAL_SERVICES`), the gateway host otherwise. A local
 * service answers on its `SERVICE_CONFIG` port unless `VITE_LOCAL_PORTS` (`service=port,...`) moves it.
 * Use it to resolve a path a backend hands back (a signed download link already carries
 * the service prefix), so a local run never opens the production gateway.
 */
export const getServiceOrigin = (service: keyof typeof SERVICE_CONFIG): string =>
  ENV === "dev" || LOCAL_SERVICES.includes(service)
    ? `http://localhost:${LOCAL_PORTS[service] ?? SERVICE_CONFIG[service].port}`
    : HOST;

const getURL = (service: keyof typeof SERVICE_CONFIG, endpoint: string) =>
  `${getServiceOrigin(service)}${SERVICE_CONFIG[service].path}${endpoint}`;

export const getUserToken = () => {
  return (
    getStorage().getItem(LOCAL_STORAGE_KEY.TOKEN) ||
    encodeURI(
      new URLSearchParams(window.location.search).get(
        LOCAL_STORAGE_KEY.TOKEN
      ) || ""
    )
  );
};

let isRedirectingToLogin = false;

/**
 * The shared answer to a failed call: a 401 clears the session and goes to
 * login; a 409 (and a 404 on /taxpayers) RESOLVES with the error body instead of
 * throwing. Exported for callers that pass `skipAccountError` because a 409
 * means something to them, but still want the 401 behavior.
 */
export const onAccountError = (error: any) => {
  if (error?.response?.status === 401) {
    getStorage().removeItem(LOCAL_STORAGE_KEY.TOKEN);
    getStorage().setItem(LOCAL_STORAGE_KEY.SESSION_EXPIRED, "true");
    // Guard: 401s concurrentes (varias llamadas con sesión vencida/anónima)
    // disparaban cada una location.href → storm de recargas duras que borraba
    // los query params de atribución. Redirige a lo sumo una vez y nunca si ya
    // estamos en /login.
    if (!isRedirectingToLogin && !window.location.pathname.startsWith('/login')) {
      isRedirectingToLogin = true;
      location.href = ROUTES.LOGIN;
    }
    return;
  }
  if (error?.response?.data?.http_status === 404) {
    if (error?.response?.config?.url?.includes("/taxpayers")) {
      return error?.response?.data;
    }
  }
  if (error?.response?.status === 409) {
    return error?.response?.data;
  }
  throw error;
};

export const SERVICE_CONFIG = {
  invoice: {
    port: 3010,
    path: "/api/v1/heru-core-invoice-service",
  },
  fiscalEngine: {
    port: 8081,
    path: "/api/v1/heru-fiscal-engine",
  },
  // tagging migrado a heru-user-management-engine (módulo Go). El NestJS sigue
  // vivo pero está roto para tokens con `sub` string: revienta en Prisma con 500
  // (verificado en prod el 28-jul). UME es inmune porque authmw entrega un int.
  tagging: {
    port: 3022,
    path: "/api/v1/heru-user-management-engine",
  },
  business: {
    port: 3016,
    path: "/api/v1/heru-core-bussiness-process-service",
  },
  ai: {
    port: 3013,
    path: "/api/v1/heru-ai-service",
  },
  atlas: {
    port: 3017,
    path: "/api/v1/heru-core-atlas-service",
  },
  crawl: {
    port: 3019,
    path: "/api/v1/crawl-flow",
  },
  bussiness: {
    port: 3020,
    path: "/api/v1/heru-core-bussiness-process-service",
  },
  partner: {
    port: 3021,
    path: "/api/v1/heru-core-partner-service",
  },
  extractionsEngine: {
    port: 8080,
    path: "/api/v1/heru-extractions-engine",
  },
  // Fuente única de auth, perfil de usuario y tagging: OTP, verify, merge de
  // cuentas, update-user y Google. Todo endpoint nuevo de esos dominios va aquí.
  userManagementEngine: {
    port: 3022,
    path: "/api/v1/heru-user-management-engine",
  },
  salesTransactionEngine: {
    port: 8080,
    path: "/api/v1/heru-sales-transaction-engine",
  },
  communications: {
    port: 8080,
    path: "/api/v1/heru-communications-engine",
  },
} as const;
